import axios from 'axios';
import store from '../../store/store'; // Import Redux Store

// Base URL configuration
// For production with nginx proxy: use '' (empty string)
// For direct API calls: use 'https://api-dev.drivelog365.com'
// You can also use environment variable: process.env.REACT_APP_API_URL
const baseURL = process.env.REACT_APP_API_URL || ''; // Podstawowy URL API

const ApiService = axios.create({  
  baseURL: baseURL, // Podstawowy URL API
  timeout: 5000, // Limit czasu na odpowiedź (5 sekund)
});

const ACCESS_TOKEN_SKEW_MS = 30 * 1000;
let refreshInFlight = null;
let isLoggingOut = false;

const toExpiryMs = (value) => {
  if (!value) return NaN;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : NaN;
};

const isLoginRequest = (config) =>
  typeof config?.url === 'string' && config.url.includes('/api/auth/login');

const isRefreshRequest = (config) =>
  typeof config?.url === 'string' && config.url.includes('/api/auth/access-token');

const logoutDueToExpiredSession = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  store.dispatch({ type: 'tokens/clearTokens' });
  alert('Sesja wygasła. Zaloguj się ponownie.');
  window.location.href = '/';
};

const applyAccessTokenToRequest = (config, accessToken) => {
  if (!config) return;
  config.headers = config.headers || {};
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
};

// Interceptor żądań (np. dodawanie tokenów autoryzacyjnych)
ApiService.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('Błąd w żądaniu:', error);
    return Promise.reject(error);
  }
);

ApiService.refreshTokens = async (refreshToken) => {
  try {
    const response = await ApiService.get('/api/auth/access-token', {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    });

    const data = response.data || {};
    const accessToken = data.token || data.accessToken;
    const accessTokenExpiration = data.expiresAt || data.accessTokenExpiresAt;

    store.dispatch({
      type: 'tokens/setAccessTokenWithExpiration',
      payload: {
        accessToken,
        accessTokenExpiration,
      },
    });

    console.log('Token został odświeżony.');
    return { accessToken, accessTokenExpiration };
  } catch (error) {
    console.error('Błąd podczas odświeżania tokenu:', error);
    throw error;
  }
};

const refreshAccessTokenSingleFlight = async () => {
  const { refreshToken, refreshTokenExpiration } = store.getState().tokens;
  const refreshExpiresAtMs = toExpiryMs(refreshTokenExpiration);
  const refreshStillValid =
    Boolean(refreshToken) &&
    (!Number.isFinite(refreshExpiresAtMs) || refreshExpiresAtMs > Date.now());

  if (!refreshStillValid) {
    throw new Error('No valid refresh token');
  }

  if (!refreshInFlight) {
    refreshInFlight = ApiService.refreshTokens(refreshToken).finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
};

ApiService.checkTokenExpiration = async () => {
  const { accessToken, refreshToken, accessTokenExpiration, refreshTokenExpiration } = store.getState().tokens;
  const now = Date.now();
  const accessExpiresAtMs = toExpiryMs(accessTokenExpiration);
  const refreshExpiresAtMs = toExpiryMs(refreshTokenExpiration);

  const accessNeedsRefresh =
    !accessToken ||
    !Number.isFinite(accessExpiresAtMs) ||
    accessExpiresAtMs - ACCESS_TOKEN_SKEW_MS < now;

  if (!accessNeedsRefresh) {
    console.log('Tokeny są ważne.');
    return true;
  }

  console.warn('Access token wygasł lub jest niekompletny.');

  const refreshStillValid =
    Boolean(refreshToken) &&
    (!Number.isFinite(refreshExpiresAtMs) || refreshExpiresAtMs > now);

  if (refreshStillValid) {
    console.log('Odświeżanie tokenu...');
    try {
      await refreshAccessTokenSingleFlight();
      return true;
    } catch (error) {
      console.error('Błąd podczas odświeżania tokenu:', error);
      logoutDueToExpiredSession();
      throw error;
    }
  }

  console.warn('Refresh token również wygasł. Wylogowywanie użytkownika...');
  logoutDueToExpiredSession();
  throw new Error('Session expired');
};

// Interceptor żądań (dodawanie tokenów autoryzacyjnych)
ApiService.getHeadersJsonUTF8OrDifferent = (contentType = 'application/json; charset=UTF-8') => {
  const token = store.getState().tokens.accessToken;

  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': contentType,
  };
};
ApiService.getHeaderOnlyToken = () => {
  const token = store.getState().tokens.accessToken;

  return {
    Authorization: `Bearer ${token}`,
  };
};


// Interceptor odpowiedzi (obsługa błędów + odświeżenie sesji)
ApiService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const data = error.response?.data;

    const canAttemptRefresh =
      Boolean(originalRequest) &&
      !originalRequest._retry &&
      !isLoginRequest(originalRequest) &&
      !isRefreshRequest(originalRequest) &&
      (status === 401 || status === 403);

    if (canAttemptRefresh) {
      originalRequest._retry = true;
      try {
        await refreshAccessTokenSingleFlight();
        applyAccessTokenToRequest(originalRequest, store.getState().tokens.accessToken);
        return ApiService(originalRequest);
      } catch (refreshError) {
        console.error('Nie udało się odświeżyć sesji:', refreshError);
        logoutDueToExpiredSession();
        return Promise.reject(refreshError);
      }
    }

    if (status === 401 && originalRequest?._retry) {
      logoutDueToExpiredSession();
      return Promise.reject(error);
    }

    if (isLoginRequest(originalRequest) || isRefreshRequest(originalRequest)) {
      return Promise.reject(error);
    }

    if (error.response) {
      switch (status) {
        case 400:
          console.error('Błąd 400: Nieprawidłowe żądanie', data);
          alert('Nieprawidłowe dane.');
          break;
        case 401:
          console.error('Błąd 401: Nieautoryzowany', data);
          logoutDueToExpiredSession();
          break;
        case 403:
          console.error('Błąd 403: Brak dostępu', data);
          alert('Nie masz uprawnień do wykonania tej operacji.');
          break;
        case 404:
          console.error('Błąd 404: Nie znaleziono zasobu', data);
          alert('Nie znaleziono zasobu.');
          break;
        case 500:
          console.error('Błąd 500: Błąd serwera', data);
          alert('Wystąpił błąd serwera. Spróbuj ponownie później.');
          break;
        default:
          console.error(`Błąd ${status}:`, data);
          alert('Wystąpił nieoczekiwany błąd. Spróbuj ponownie.');
      }
    } else if (error.request) {
      console.error('Brak odpowiedzi z serwera:', error.request);
      alert('Nie można połączyć się z serwerem. Sprawdź swoje połączenie internetowe.');
    } else {
      console.error('Błąd:', error.message);
      alert('Wystąpił błąd. Spróbuj ponownie.');
    }
    return Promise.reject(error);
  }
);

///-------------------------------------------------------------------------
// Funkcja logowania
ApiService.login = async (login, pass) => {
  try {
    const response = await ApiService.post('/api/auth/login', {
      login,
      pass,
    });
    return response.data; // Zwróć dane odpowiedzi (np. tokeny)
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};

///-------------------------------------------------------------------------
// users

ApiService.getUsers = async (sortBy, sortDirection, pageNumber, searchQuery) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

  try {
    const payload = {
      ...(sortBy !== null && sortDirection !== null && { sortBy }),
      ...(sortBy !== null && sortDirection !== null && { sortDirection }),
      ...(searchQuery !== null && { searchQuery }),
      pageNumber
    }
    const response = await ApiService.get('/api/admin/get-profiles', {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
      params: payload,
      });
    return response.data; // Zwróć dane użytkowników
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};

ApiService.getSportIcon = async (id) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/admin/sports/find-icon-by-id/' + id , {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
    });
    return response.data;
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};

ApiService.getUserData = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/admin/profile/full_with_logins/' + id , {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response.data;
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

/** GET /api/admin/statistics/perUser/{userId} – per-user statistics for admin (notes, invoices, vehicleCheck, etc.) */
ApiService.getPerUserStatistics = async (userId) => {
    await ApiService.checkTokenExpiration();
    try {
        const response = await ApiService.get(`/api/admin/statistics/perUser/${userId}`, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.updateUserRole = async (userId, role) => {
    await ApiService.checkTokenExpiration();

    try {
        const token = store.getState().tokens.accessToken;
        if (!token) {
            throw new Error('No access token available');
        }
        
        console.log('Updating user role:', userId, 'to', role, 'with token:', token.substring(0, 20) + '...');
        
        const response = await ApiService.patch(`/api/admin/users/${userId}/role`, {
            role: role
        }, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        
        console.log('Role update successful:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error updating user role:', error);
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

///-------------------------------------------------------------------------
// sports

ApiService.getSports = async (sortBy, sortDirection, keyword, pageNumber) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();
    
  try {
    const payload = {
      ...(sortBy !== null && sortDirection !== null && { sortBy }),
      ...(sortBy !== null && sortDirection !== null && { sortDirection }),
      ...(keyword && { keyword }),
      pageNumber
    }
    const response = await ApiService.get('/api/admin/sports/get-sports', {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
      params: payload,
      });
    return response.data; // Zwróć dane użytkowników
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};

ApiService.getTrainings = async (sortBy, sortDirection, keyword, pageNumber) => {
  // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
  await ApiService.checkTokenExpiration();

  try {
    const payload = {
      ...(sortBy !== null && sortDirection !== null && { sortBy }),
      ...(sortBy !== null && sortDirection !== null && { sortDirection }),
      ...(keyword && { keyword }),
      pageNumber
    }
    const response = await ApiService.get('/api/admin/training', {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
      params: payload,
    });
    return response.data; // Zwróć dane użytkowników
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};

ApiService.getOpinionsU = async (sortBy, sortDirection, keyword, pageNumber) => {
  // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
  await ApiService.checkTokenExpiration();

  try {
    const payload = {
      ...(sortBy !== null && sortDirection !== null && { sortBy }),
      ...(sortBy !== null && sortDirection !== null && { sortDirection }),
      // ...(keyword && { keyword }),
      pageNumber
    }
    const response = await ApiService.get('/api/opinion/partners', {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
      params: payload,
    });
    return response.data; // Zwróć dane użytkowników
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};

ApiService.getOpinionsT = async (sortBy, sortDirection, keyword, pageNumber) => {
  // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
  await ApiService.checkTokenExpiration();

  try {
    const payload = {
      ...(sortBy !== null && sortDirection !== null && { sortBy }),
      ...(sortBy !== null && sortDirection !== null && { sortDirection }),
      // ...(keyword && { keyword }),
      pageNumber
    }
    const response = await ApiService.get('/api/opinion/trainings', {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
      params: payload,
    });
    return response.data; // Zwróć dane użytkowników
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};

ApiService.moveSportUp = async (sportOrder) => {
  // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
  await ApiService.checkTokenExpiration();

const token =  store.getState().tokens.accessToken;
const path = `/api/admin/sports/order-move-up?order=${sportOrder}`;
try {
  const response = await ApiService.patch(path, {}, {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
      });  
} catch (error) {
  throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
}
};

ApiService.moveSportDown = async (sportOrder) => {
  // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
  await ApiService.checkTokenExpiration();

const token =  store.getState().tokens.accessToken;
const path = `/api/admin/sports/order-move-down?order=${sportOrder}`;
try {
  const response = await ApiService.patch(path, {}, {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka      
      });  
} catch (error) {
  throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
}
};

ApiService.addNewSport = async (sportData) => {
  await ApiService.checkTokenExpiration();
  
  try {
    const response = await ApiService.post('/api/admin/sports/add-new', sportData, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
      });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.geoCoding = async (longitude, latitude) => {
  await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/resolve-location`, {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
            params: {
                longitude,
                latitude
            }
        });

        return response.data; // Zwróć dane odpowiedzi
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
}

ApiService.addNewUser = async (data) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.post('/api/admin/register', data, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getExcelReport = async (fromDate, toDate) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/admin/activity/excel-report/${fromDate}/${toDate}`, {
            headers: ApiService.getHeaderOnlyToken(),
            responseType: 'blob', // Ważne dla plików Excel
        });
        return response;
    } catch (error) {
        throw error;
    }
};


ApiService.toggleHasBlocked = async (userId) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch(`/api/admin/toggle-user-blocked/${userId}`, {}, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return null;
    } catch (error) {
        throw error;
    }
};

ApiService.getHasBlocked = async (userId) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/admin/user-has-blocked/${userId}`,{
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getSportById = async (sportId) => {
  // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/admin/sports/find-by/id/${sportId}`, {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
      });
    return response.data; // Zwróć dane odpowiedzi
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
}
ApiService.toggleSportActive = async (sportId) => {
  // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.patch(`/api/admin/sports/toggle-activation?id=${sportId}`, {}, {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka    
      });
    return response.data; // Zwróć dane odpowiedzi
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
}
ApiService.updateSport = async (sportData) => {
  await ApiService.checkTokenExpiration();

  const token = store.getState().tokens.accessToken;

  try {
    const response = await ApiService.post(`/api/admin/sports/update`, sportData, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(), 
      });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.deleteSportsByIdList = async (idList) => {
  await ApiService.checkTokenExpiration();

  const token = store.getState().tokens.accessToken;

  try {
    const response = await ApiService.delete('/api/admin/sports/delete/by-id-list', {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data: idList, // Lista ID w ciele zapytania
    });

    return response // Zwróć odpowiedź serwera
  } catch (error) {
    throw error; // Obsłuż błędy w miejscu wywołania
  }
};

ApiService.setSortListEndpoint = async (sort) => {
await ApiService.checkTokenExpiration();

  const token = store.getState().tokens.accessToken;

  try {
    const response = await ApiService.put('/api/admin/app_config/sport_sort_config/'+sort,{}, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },      
    });

    return response // Zwróć odpowiedź serwera
  } catch (error) {
    throw error; // Obsłuż błędy w miejscu wywołania
  }
};

ApiService.getSortListEndpoint = async () => {
await ApiService.checkTokenExpiration();

  const token = store.getState().tokens.accessToken;

  try {
    const response = await ApiService.get('/api/admin/app_config/sport_sort_config',
      {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
      });
    return response.data // Zwróć odpowiedź serwera
  } catch (error) {
    throw error; // Obsłuż błędy w miejscu wywołania
  }
};

///-------------------------------------------------------------------------
// images
ApiService.uploadImage = async (file) => {
  // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne
  await ApiService.checkTokenExpiration();

  const token = store.getState().tokens.accessToken;
  const formData = new FormData();
  formData.append('file', file); // Dodanie pliku do formData

  try {
    const response = await ApiService.post('/api/admin/image-upload', formData, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent('multipart/form-data'),
    });
    return response.data; // Zwróć ciąg znaków zwrócony przez endpoint
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};


///-------------------------------------------------------------------------
/// notifications

// Get notifications list with filters, sorting, and pagination
ApiService.getNotifications = async (params) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/admin/notifications', {
      headers: ApiService.getHeaderOnlyToken(),
      params: params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get notification by ID
ApiService.getNotificationById = async (id) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/admin/notifications/${id}`, {
      headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Create notification
ApiService.createNotification = async (notificationData) => {
  await ApiService.checkTokenExpiration();
  
  try {
    const response = await ApiService.post('/api/admin/notifications', notificationData, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update notification
ApiService.updateNotification = async (id, notificationData) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.put(`/api/admin/notifications/${id}`, notificationData, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete notification
ApiService.deleteNotification = async (id) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.delete(`/api/admin/notifications/${id}`, {
      headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get notifications by user ID
ApiService.getNotificationsByUserId = async (userId) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/admin/notifications/user/${userId}`, {
      headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Legacy method for backward compatibility
ApiService.addNewNotification = ApiService.createNotification;

ApiService.getNotificationTypes = async () => {
await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/admin/notifications/types',
      {
      headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
      });
    return response.data // Zwróć odpowiedź serwera
  } catch (error) {
    throw error; // Obsłuż błędy w miejscu wywołania
  }
};

ApiService.getStatistics = async () => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/admin/activity',
        {
          headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
    return response.data // Zwróć odpowiedź serwera
  } catch (error) {
    throw error; // Obsłuż błędy w miejscu wywołania
  }
};

/** Full dashboard: user activity, counts, premium, Stripe revenue, vehicle checks, notes/documents, timesheets, engagement. ROLE_ADMIN only. */
ApiService.getAdminStatistics = async () => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/admin/statistics', {
      headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getFaq = async () => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/faq/by-admin',
            {
                headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
            });
        return response.data // Zwróć odpowiedź serwera
    } catch (error) {
        throw error; // Obsłuż błędy w miejscu wywołania
    }
};

ApiService.addNewFaq = async (data) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.post('/api/faq', data, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.deleteFaq = async (id) => {
    await ApiService.checkTokenExpiration();

    const token = store.getState().tokens.accessToken;

    try {
        const response = await ApiService.delete('/api/faq/' + id , {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        return response // Zwróć odpowiedź serwera
    } catch (error) {
        throw error; // Obsłuż błędy w miejscu wywołania
    }
};

///-------------------------------------------------------------------------
// documents

ApiService.getDocuments = async (params) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/admin/documents', {
            headers: ApiService.getHeaderOnlyToken(),
            params: params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getDocumentsCount = async (params) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/admin/documents/count', {
            headers: ApiService.getHeaderOnlyToken(),
            params: params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.previewDocument = async (category, id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/admin/documents/${category}/${id}/preview`, {
            headers: ApiService.getHeaderOnlyToken(),
            responseType: 'blob',
        });
        // Create blob URL and open in new tab
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Clean up the URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        return response;
    } catch (error) {
        throw error;
    }
};

ApiService.downloadDocument = async (category, id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/admin/documents/${category}/${id}/download`, {
            headers: ApiService.getHeaderOnlyToken(),
            responseType: 'blob',
        });
        // Create blob URL and trigger download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${category}-${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up the URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        return response;
    } catch (error) {
        throw error;
    }
};

ApiService.bulkDeleteDocuments = async (documentIds) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.delete('/api/admin/documents/bulk', {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
            data: { documentIds },
        });
        return response;
    } catch (error) {
        throw error;
    }
};

///-------------------------------------------------------------------------
// encyclopedia articles

ApiService.getArticles = async (params) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/admin/articles', {
            headers: ApiService.getHeaderOnlyToken(),
            params: params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.createArticle = async (articleData) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.post('/api/articles', articleData, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getArticleById = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/admin/articles/${id}`, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.acceptArticle = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch(`/api/admin/articles/${id}/accept`, {}, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.rejectArticle = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch(`/api/admin/articles/${id}/reject`, {}, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.updateArticleStatus = async (id, status) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch(`/api/admin/articles/${id}/status`, { status }, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.updateArticle = async (id, articleData) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.put(`/api/admin/articles/${id}`, articleData, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.deleteArticle = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.delete(`/api/admin/articles/${id}`, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.bulkDeleteArticles = async (articleIds) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.delete('/api/admin/articles/bulk', {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
            data: { article_ids: articleIds },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getArticlesByStatus = async (status) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/admin/articles/status/${status}`, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getPendingArticles = async () => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/admin/articles/pending', {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export default ApiService;
